import React from 'react';

import {
  AssignmentCategoryEnum,
  AssignmentEditUserResponseModel,
  AssignmentTypeEnum,
  BOUsersPriceAgreementRespModel,
  CustomerResponseModel,
} from 'api/backofficeApiClient';
import AssignmentModal, {
  AssignmentInitialValues,
} from 'components/modals/AssignmentDataModal/AssignmentModal';
import {
  mapICAssignmentDetailsToCorporateInitialValues,
  mapICAssignmentDetailsToInitialValues,
  mapSCAssignmentDetailsToCorporateInitialValues,
  mapSCAssignmentDetailsToInitialValues,
  mapW2AssignmentDetailsToCorporateInitialValues,
  mapW2AssignmentDetailsToInitialValues,
} from 'features/assignment/AssignmentDetailsPage/utils';
import { AssignmentCreationTypeEnum } from 'components/modals/AssignmentDataModal/enum';
import { AssignmentEditAdminResponseModel } from 'api/adminApiClient';
import { useAppSelector } from 'hooks/useAppSelector';
import { RehireTypeEnum } from '../../RehireTypeSelectionModal';
import { payCodesSelector } from 'redux/selectors/assignmentSelectors';

type Props = {
  onSubmit: () => void;
  onClose: () => void;
  initialRehireAssignmentInfoValues:
    | AssignmentEditUserResponseModel
    | AssignmentEditAdminResponseModel;
  priceAgreement?: BOUsersPriceAgreementRespModel;
  customers: CustomerResponseModel[];
  activeRehireType?: RehireTypeEnum;
  isCustomersLoaded: boolean;
  hasRehireDateRestriction: boolean;
};

export default function RehireAssignmentModal(props: Props) {
  const {
    onClose,
    onSubmit,
    initialRehireAssignmentInfoValues,
    priceAgreement,
    customers,
    activeRehireType,
    isCustomersLoaded,
    hasRehireDateRestriction,
  } = props;
  const payCodesDetails = useAppSelector(payCodesSelector);

  const getAssignmentDataModalByType = () => {
    const { assignmentCategory, assignmentType } =
      initialRehireAssignmentInfoValues;

    if (!assignmentType || !assignmentCategory) return null;

    const isRehireWithNewCustomer =
      activeRehireType === RehireTypeEnum.NewCustomer;

    const isCorporate = assignmentCategory === AssignmentCategoryEnum.Corporate;
    const isRecruiter = assignmentCategory === AssignmentCategoryEnum.Recruiter;

    let initialValues: AssignmentInitialValues | null = null;

    switch (assignmentType) {
      case AssignmentTypeEnum.W2Assignment: {
        switch (assignmentCategory) {
          case AssignmentCategoryEnum.Corporate: {
            initialValues = mapW2AssignmentDetailsToCorporateInitialValues(
              initialRehireAssignmentInfoValues,
              payCodesDetails,
              true,
            );
            break;
          }
          case AssignmentCategoryEnum.Recruiter: {
            initialValues = mapW2AssignmentDetailsToInitialValues(
              initialRehireAssignmentInfoValues,
              payCodesDetails,
              true,
              isRehireWithNewCustomer,
            );
            break;
          }
        }
        break;
      }

      case AssignmentTypeEnum.ICAssignment: {
        switch (assignmentCategory) {
          case AssignmentCategoryEnum.Corporate: {
            initialValues = mapICAssignmentDetailsToCorporateInitialValues(
              initialRehireAssignmentInfoValues,
              payCodesDetails,
              true,
            );
            break;
          }
          case AssignmentCategoryEnum.Recruiter: {
            initialValues = mapICAssignmentDetailsToInitialValues(
              initialRehireAssignmentInfoValues,
              payCodesDetails,
              true,
              isRehireWithNewCustomer,
            );
            break;
          }
        }
        break;
      }

      case AssignmentTypeEnum.SCAssignment: {
        switch (assignmentCategory) {
          case AssignmentCategoryEnum.Corporate: {
            initialValues = mapSCAssignmentDetailsToCorporateInitialValues(
              initialRehireAssignmentInfoValues,
              payCodesDetails,
              true,
            );
            break;
          }
          case AssignmentCategoryEnum.Recruiter: {
            initialValues = mapSCAssignmentDetailsToInitialValues(
              initialRehireAssignmentInfoValues,
              payCodesDetails,
              true,
              isRehireWithNewCustomer,
            );
            break;
          }
        }
        break;
      }
    }

    if (!initialValues) return null;

    return (
      <AssignmentModal
        onClose={onClose}
        onSubmit={onSubmit}
        type={assignmentType}
        isDraftAvailable={false}
        isCorporateTA={isCorporate}
        isRecruiter={isRecruiter}
        initialValues={initialValues}
        assignmentId={initialRehireAssignmentInfoValues?.assignmentId}
        assignmentCreationType={AssignmentCreationTypeEnum.Rehire}
        memberId={
          (
            initialRehireAssignmentInfoValues as AssignmentEditAdminResponseModel
          )?.memberId
        }
        companyId={
          (
            initialRehireAssignmentInfoValues as AssignmentEditAdminResponseModel
          )?.memberCompanyId
        }
        workerFileInfo={
          initialRehireAssignmentInfoValues.assignmentDocument?.workerInfo
            ?.workerFile
        }
        contractorCompanyFileInfo={
          initialRehireAssignmentInfoValues.assignmentDocument
            ?.contractorCompanyInfo?.workerFile
        }
        priceAgreement={priceAgreement}
        customers={customers}
        activeRehireType={activeRehireType}
        isCustomersLoaded={isCustomersLoaded}
        hasRehireDateRestriction={hasRehireDateRestriction}
      />
    );
  };

  return <>{getAssignmentDataModalByType()}</>;
}
